import React, { useEffect, useState } from "react";
import { Search, MessageSquarePlus, Users, Check, UserPlus, Radio } from "lucide-react";
import { toast } from "sonner";
import Modal from "@/components/chat/Modal";
import InviteDialog from "@/components/chat/InviteDialog";
import { getInitials, avatarColor } from "@/lib/avatar";
import api from "@/lib/api";

export default function NewChatDialog({ onClose, onStartChat, onCreateGroup, onCreateChannel, onJoinChannel }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [contactIds, setContactIds] = useState(() => new Set());
  const [mode, setMode] = useState("direct"); // direct | group | channel
  const groupMode = mode === "group";
  const [groupName, setGroupName] = useState("");
  const [selected, setSelected] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [chName, setChName] = useState("");
  const [chDesc, setChDesc] = useState("");
  const [chPublic, setChPublic] = useState(true);
  const [discover, setDiscover] = useState([]);

  useEffect(() => {
    const id = setTimeout(async () => {
      try {
        if (q.trim()) {
          const { data } = await api.get("/users", { params: { q: q.trim() } });
          setUsers(data);
        } else {
          const { data } = await api.get("/users/contacts");
          setUsers(data);
        }
      } catch { setUsers([]); }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    if (mode === "channel") api.get("/channels/discover").then(({ data }) => setDiscover(data)).catch(() => {});
  }, [mode]);

  useEffect(() => {
    api.get("/users/contacts").then(({ data }) => setContactIds(new Set(data.map((u) => u.id)))).catch(() => {});
  }, []);

  const addContact = async (e, u) => {
    e.stopPropagation();
    setContactIds((s) => new Set(s).add(u.id));
    try { await api.post(`/users/${u.id}/contact`); toast.success(`${u.name} added to contacts`); }
    catch { setContactIds((s) => { const n = new Set(s); n.delete(u.id); return n; }); toast.error("Could not add contact"); }
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <Modal title={mode === "group" ? "New group" : mode === "channel" ? "Channels" : "New chat"} onClose={onClose} bare>
      <div className="flex-1 min-h-0 flex flex-col max-w-2xl mx-auto w-full px-5 pt-4 pb-4 rc-safe-bottom">
      <div className="shrink-0">
      <button onClick={() => setInviteOpen(true)} data-testid="open-invite-btn"
        className="w-full flex items-center gap-3 p-3 mb-4 rounded-2xl border border-[var(--rc-gold)]/30 bg-[var(--rc-gold)]/5 text-left">
        <span className="h-10 w-10 grid place-items-center rounded-full bg-[var(--rc-gold)]/15 text-[var(--rc-gold)]"><UserPlus size={18} /></span>
        <span className="flex-1"><span className="block font-medium">Invite friends</span><span className="block text-xs text-[var(--rc-muted)]">See who's on RoyalChat Nova & invite others</span></span>
      </button>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setMode("direct")} data-testid="mode-direct-btn" className={`flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium transition-colors ${mode === "direct" ? "bg-[var(--rc-gold)] text-black" : "bg-[var(--rc-bg)] text-[var(--rc-muted)]"}`}><MessageSquarePlus size={16} /> Direct</button>
        <button onClick={() => setMode("group")} data-testid="mode-group-btn" className={`flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium transition-colors ${mode === "group" ? "bg-[var(--rc-gold)] text-black" : "bg-[var(--rc-bg)] text-[var(--rc-muted)]"}`}><Users size={16} /> Group</button>
        <button onClick={() => setMode("channel")} data-testid="mode-channel-btn" className={`flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium transition-colors ${mode === "channel" ? "bg-[var(--rc-gold)] text-black" : "bg-[var(--rc-bg)] text-[var(--rc-muted)]"}`}><Radio size={16} /> Channel</button>
      </div>

      {groupMode && (
        <input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name" data-testid="group-name-input"
          className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-2xl px-4 py-3 mb-3 focus:outline-none focus:border-[var(--rc-gold)]/50" />
      )}

      {mode !== "channel" && (
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--rc-muted)]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or phone…" data-testid="user-search-input"
          className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
      </div>
      )}
      </div>

      {mode === "channel" ? (
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4 mb-4 space-y-3">
            <p className="text-sm font-medium">Create a channel</p>
            <input value={chName} onChange={(e) => setChName(e.target.value)} placeholder="Channel name" data-testid="channel-name-input"
              className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-4 py-2.5 focus:outline-none focus:border-[var(--rc-gold)]/50" />
            <input value={chDesc} onChange={(e) => setChDesc(e.target.value)} placeholder="Description (optional)" data-testid="channel-desc-input"
              className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={chPublic} onChange={(e) => setChPublic(e.target.checked)} data-testid="channel-public-toggle" /> Public (anyone can discover & join)</label>
            <button disabled={!chName.trim()} onClick={() => onCreateChannel({ name: chName.trim(), description: chDesc.trim() || null, public: chPublic })} data-testid="create-channel-btn"
              className="w-full rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-2.5 disabled:opacity-50">Create channel</button>
          </div>
          <p className="text-sm font-medium mb-2">Discover channels</p>
          {discover.length === 0 && <p className="text-center text-sm text-[var(--rc-muted)] py-6">No public channels yet</p>}
          <div className="space-y-1">
            {discover.map((c) => (
              <div key={c.id} data-testid={`discover-channel-${c.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-[var(--rc-bg)]">
                <div className="h-10 w-10 rounded-full grid place-items-center text-[var(--rc-gold)] overflow-hidden border-2 border-[var(--rc-gold)]" style={{ backgroundColor: avatarColor(c.name) }}>
                  {c.avatar ? <img src={c.avatar} alt="" className="h-full w-full object-cover" /> : <Radio size={18} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-xs text-[var(--rc-muted)] truncate">{c.subscribers} subscribers{c.description ? ` · ${c.description}` : ""}</div>
                </div>
                <button onClick={() => onJoinChannel(c.id)} data-testid={`join-channel-${c.id}`} className="shrink-0 rounded-full bg-[var(--rc-gold)] text-black text-xs font-semibold px-4 py-2">Join</button>
              </div>
            ))}
          </div>
        </div>
      ) : (
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-1">
        {!q.trim() && (
          <p className="px-3 pt-1 pb-2 text-xs font-medium uppercase tracking-wide text-[var(--rc-muted)]" data-testid="contacts-label">Your contacts</p>
        )}
        {users.length === 0 && (
          <p className="text-center text-sm text-[var(--rc-muted)] py-8" data-testid="user-list-empty">
            {q.trim() ? "No users found" : "No contacts yet — search by name, email or phone to start a chat"}
          </p>
        )}
        {users.map((u) => (
          <div key={u.id} data-testid={`user-${u.id}`}
            onClick={() => (groupMode ? toggle(u.id) : onStartChat(u.id))}
            className="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-[var(--rc-bg)] cursor-pointer">
            <div className="h-10 w-10 rounded-full grid place-items-center text-[var(--rc-gold)] text-sm font-semibold overflow-hidden border-2 border-[var(--rc-gold)]" style={{ backgroundColor: avatarColor(u.name) }}>
              {u.avatar ? <img src={u.avatar} alt="" className="h-full w-full object-cover" /> : getInitials(u.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{u.name} {u.flag}</div>
              <div className="text-xs text-[var(--rc-muted)] truncate">{u.email}</div>
            </div>
            {groupMode && (
              <div className={`h-6 w-6 rounded-full border grid place-items-center ${selected.includes(u.id) ? "bg-[var(--rc-gold)] border-[var(--rc-gold)] text-black" : "border-[var(--rc-border)]"}`}>
                {selected.includes(u.id) && <Check size={14} />}
              </div>
            )}
            {!groupMode && (
              contactIds.has(u.id) ? (
                <span className="shrink-0 flex items-center gap-1 text-xs text-[var(--rc-muted)]" data-testid={`contact-saved-${u.id}`}><Check size={13} /> Contact</span>
              ) : (
                <button onClick={(e) => addContact(e, u)} data-testid={`add-contact-${u.id}`}
                  className="shrink-0 flex items-center gap-1 rounded-full border border-[var(--rc-gold)]/60 text-[var(--rc-gold)] text-xs font-medium px-2.5 py-1.5 hover:bg-[var(--rc-gold)]/10">
                  <UserPlus size={13} /> Add
                </button>
              )
            )}
          </div>
        ))}
      </div>
      )}

      {groupMode && (
        <button disabled={!groupName || selected.length === 0} onClick={() => onCreateGroup(groupName, selected)} data-testid="create-group-btn"
          className="w-full mt-4 shrink-0 rounded-2xl bg-[var(--rc-gold)] text-black font-semibold py-3 disabled:opacity-50">
          Create group ({selected.length})
        </button>
      )}
      </div>
      {inviteOpen && <InviteDialog onClose={() => setInviteOpen(false)} onStartChat={onStartChat} />}
    </Modal>
  );
}
